import type { ItemGroup, ItemView } from '@fall26/shared'
import { useEffect, useMemo, useState } from 'react'
import { useCourses, useItems, useSetDone } from '../api/hooks.js'
import { CourseDot } from '../components/CourseDot.js'
import { ItemRow } from '../components/ItemRow.js'
import { ItemSheet } from '../components/item/ItemSheet.js'

/**
 * Everything, grouped.
 *
 * THE ROW DOES NOT MOVE WHEN YOU TICK IT.
 *
 * Grouping is captured once, when the data arrives, and held. Ticking an item
 * changes how it LOOKS — struck through, checkbox filled — and nothing else:
 * it keeps its position in its original group. Regrouping happens on an
 * explicit refresh or when the tab is left and returned to.
 *
 * The alternative, regrouping live, means the thing you just tapped vanishes
 * from under your thumb and the next row slides up into the tap you were
 * already making. On a phone, walking, that is how you tick the wrong item.
 */

const GROUP_ORDER: ItemGroup[] = ['overdue', 'this-week', 'later', 'needs-date', 'done']

const GROUP_LABEL: Record<ItemGroup, string> = {
  overdue: 'Overdue',
  'this-week': 'This week',
  later: 'Later',
  'needs-date': 'Needs a date',
  done: 'Done',
}

const GROUP_TONE: Record<ItemGroup, string> = {
  overdue: 'text-[var(--overdue)]',
  'this-week': 'text-[var(--accent)]',
  later: 'text-[var(--text-muted)]',
  'needs-date': 'text-[var(--unknown)]',
  done: 'text-[var(--done)]',
}

type Snapshot = Record<ItemGroup, number[]>

function snapshotOf(items: ItemView[]): Snapshot {
  const snapshot: Snapshot = { overdue: [], 'this-week': [], later: [], 'needs-date': [], done: [] }
  for (const item of items) {
    // Fall back rather than crash: an older server that does not send `group`
    // should degrade to one flat list, not take the whole screen down.
    const bucket = snapshot[item.group] ?? snapshot.later
    bucket.push(item.id)
  }
  return snapshot
}

export function Work() {
  const items = useItems('?includeDone=true')
  const courses = useCourses()
  const setDone = useSetDone()

  const [course, setCourse] = useState<string | null>(null)
  const [editing, setEditing] = useState<number | null>(null)
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [regroupable, setRegroupable] = useState(false)

  // First arrival fixes the layout. Mounting this route again — which is what
  // a tab change does — starts over, which is the "regroup on tab change" rule.
  useEffect(() => {
    if (items.data && snapshot === null) setSnapshot(snapshotOf(items.data.items))
  }, [items.data, snapshot])

  const byId = useMemo(
    () => new Map((items.data?.items ?? []).map((i) => [i.id, i])),
    [items.data],
  )
  const hue = useMemo(() => {
    const map = new Map<string, number>((courses.data?.courses ?? []).map((c) => [String(c.id), c.colorIndex]))
    return (id: string) => map.get(id) ?? 0
  }, [courses.data])

  if (items.isError) {
    return (
      <p className="rounded-[var(--radius)] border border-[var(--overdue)]/30 bg-[var(--overdue-wash)] p-4 text-sm text-[var(--overdue)]">
        Could not load your work.
      </p>
    )
  }
  if (!items.data || !snapshot) return <Skeleton />

  const regroup = () => {
    setSnapshot(snapshotOf(items.data.items))
    setRegroupable(false)
  }

  const toggle = (item: ItemView, done: boolean) => {
    setDone.mutate({ itemId: item.id, done })
    // The row stays put; this only lights up the button that moves it.
    setRegroupable(true)
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--text)]">Work</h1>
        {/*
          * `tap` matters here even though the button is small and quiet. It is
          * the control you reach for right after ticking things off, with the
          * same thumb, and a 27px-tall target is one you miss — which on this
          * screen means hitting a checkbox instead.
          */}
        <button
          type="button"
          onClick={regroup}
          disabled={!regroupable}
          className={`tap tabular rounded-[var(--radius)] border px-2.5 text-[11px] uppercase tracking-[0.08em] transition-colors ${
            regroupable
              ? 'border-[var(--accent-line)] bg-[var(--accent-wash)] text-[var(--accent)]'
              : 'border-transparent text-[var(--text-subtle)]'
          }`}
        >
          {regroupable ? 'Regroup' : 'Up to date'}
        </button>
      </div>

      <CourseFilter
        courses={courses.data?.courses ?? []}
        selected={course}
        onSelect={setCourse}
        hue={hue}
      />

      {editing !== null && byId.get(editing) && (
        <ItemSheet item={byId.get(editing)!} onClose={() => setEditing(null)} />
      )}

      {GROUP_ORDER.map((group) => {
        const rows = snapshot[group]
          .map((id) => byId.get(id))
          .filter((i): i is ItemView => i !== undefined)
          .filter((i) => course === null || i.courseId === course)

        if (rows.length === 0) return null

        return (
          <div key={group}>
            <div className="mb-1.5 flex items-baseline gap-2">
              <h2 className={`tabular text-[11px] uppercase tracking-[0.14em] ${GROUP_TONE[group]}`}>
                {GROUP_LABEL[group]}
              </h2>
              <span className="tabular text-[11px] text-[var(--text-subtle)]">{rows.length}</span>
              {group === 'needs-date' && (
                <span className="text-[11px] text-[var(--text-subtle)]">
                  — no date from the syllabus yet
                </span>
              )}
            </div>
            <ul className="space-y-1.5">
              {rows.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  colorIndex={hue(item.courseId)}
                  onToggle={(done) => toggle(item, done)}
                  onOpen={() => setEditing(item.id)}
                  // Only where it is the whole point of the group. A "set a
                  // date" control on every row would be noise on the 103 rows
                  // that already have one.
                  quickDate={group === 'needs-date'}
                />
              ))}
            </ul>
          </div>
        )
      })}
    </section>
  )
}

function CourseFilter({
  courses,
  selected,
  onSelect,
  hue,
}: {
  courses: Array<{ id: string; code: string; colorIndex: number }>
  selected: string | null
  onSelect: (id: string | null) => void
  hue: (id: string) => number
}) {
  return (
    <div className="-mx-4 overflow-x-auto px-4">
      <div className="flex gap-1.5 pb-1">
        <FilterChip active={selected === null} onClick={() => onSelect(null)}>
          All
        </FilterChip>
        {courses.map((c) => (
          <FilterChip key={c.id} active={selected === c.id} onClick={() => onSelect(c.id)}>
            <CourseDot colorIndex={hue(String(c.id))} />
            {c.code}
          </FilterChip>
        ))}
      </div>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tap tabular flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[11px] uppercase tracking-[0.06em] transition-colors ${
        active
          ? 'border-[var(--accent-line)] bg-[var(--accent-wash)] text-[var(--accent)]'
          : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)]'
      }`}
    >
      {children}
    </button>
  )
}

function Skeleton() {
  return (
    <div className="space-y-3">
      <div className="h-8 w-32 animate-pulse rounded bg-[var(--surface-sunken)]" />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-16 animate-pulse rounded-[var(--radius)] bg-[var(--surface-sunken)]" />
      ))}
    </div>
  )
}
